import { UIEvent, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsSmallScreen from '@hooks/element-size-hooks/useIsSmallScreen';
import { selectedDateWithoutTime, selectGlobalSearch, selectSearchTypes, selectSearchDateRange } from '@store/global/selectors';
import { setGlobalSearch } from '@store/global/slice';
import { setOrder, setOrdersAndPagingInfo, resetSearchStore } from '@pages/orders/store/slice';
import { selectSearchSingleOrderUnvId, selectOrdersPagingInfo } from '@pages/orders/store/selectors';
import { PagingOrdersData } from '@api/shipping-data/types/PagingOrdersData';
import { WebOrderStatistics } from '@api/shipping-data/types/WebOrderStatistics';
import { useLazyGetFulltextSearchQuery, useLazyGetSingleOrderQuery, useResetSingleOrderMutation } from '@api/shipping-data/ordersApi';
import { useResetCustomerOrdersMutation, useResetCustomerInfoMutation, useResetOrderTimelineMutation } from '@api/shipping-data/detailApi';
import { AppPage } from 'components/app-page/AppPage';
import ListArea from '@pages/orders/orders-page/list-area/ListArea';
import Detail from '@pages/orders/detail/Detail';



export const Search = () => {

	const pageRef = useRef<HTMLDivElement>(null);
	const preserveSelectedOrder = useRef<boolean>(false);
	const isSmallScreen = useIsSmallScreen();
	const dateWithoutTime = useSelector(selectedDateWithoutTime);
	const globalSearch = useSelector(selectGlobalSearch);
	const searchTypes = useSelector(selectSearchTypes);
	const searchDateRange = useSelector(selectSearchDateRange);
	const searchSingleOrderUnvId = useSelector(selectSearchSingleOrderUnvId);
	const pagingInfo = useSelector(selectOrdersPagingInfo);

	const [ ordersScrollTop, setOrdersScrollTop ] = useState(0);
	const [ ordersTop, setOrdersTop ] = useState(0);
	const [ ordersHeight, setOrdersHeight ] = useState(0);
	const [ isScrolledDown, setIsScrolledDown ] = useState(false);

	const dispatch = useDispatch();
	const updateOrder = (order: WebOrderStatistics) => dispatch(setOrder(order));
	const updateOrdersAndPagingInfo = (pagingOrdersData: PagingOrdersData) => dispatch(setOrdersAndPagingInfo(pagingOrdersData));
	const dispatchResetSearchStore = () => dispatch(resetSearchStore({ preserveSelectedOrderUnvId: preserveSelectedOrder.current }));
	const resetGlobalSearch = () => dispatch(setGlobalSearch(null));
	
	const [ getFulltextSearch, { data, isLoading, isFetching, isError } ] = useLazyGetFulltextSearchQuery();
	const [ getSingleOrder, { data: soData, isLoading: soIsLoading, isFetching: soIsFetching, isError: soIsError } ] = useLazyGetSingleOrderQuery();
	const [ resetSingleOrder ] = useResetSingleOrderMutation();
	const [ resetCustomerOrders ] = useResetCustomerOrdersMutation();
	const [ resetCustomerInfo ] = useResetCustomerInfoMutation();
	const [ resetOrderTimeline ] = useResetOrderTimelineMutation();


	useEffect(() => {
		if (pageRef && pageRef.current) {
			// init scroll values, before scroll event is triggered
			setOrdersTop(pageRef.current.getBoundingClientRect().top);
			setOrdersHeight(pageRef.current.clientHeight);
		}
		return () => {
			dispatchResetSearchStore();
			resetGlobalSearch();
		}
	}, []);

	
	useEffect(() => {
		if (dateWithoutTime && searchSingleOrderUnvId && searchSingleOrderUnvId.length > 0) {
			getSingleOrder(searchSingleOrderUnvId, false);
			return;
		}
	}, [ dateWithoutTime, searchSingleOrderUnvId ]);


	useEffect(() => {
		getFulltextSearchInit();
	}, [ globalSearch, searchTypes, searchDateRange ]);


	useEffect(() => {
		if (isScrolledDown) {
			getFulltextSearchNextPage();
		}
	}, [ isScrolledDown ]);


	const getFulltextSearchInit = useCallback(() => {
		if (globalSearch) {
			getFulltextSearch({
				dateFrom: searchDateRange.from,
				dateTo: searchDateRange.to,
				searchTypes: searchTypes,
				fulltext: globalSearch,
			}, false);
		}
	},[ globalSearch, searchTypes, searchDateRange ]);
	
	
	const getFulltextSearchNextPage = useCallback(() => {
		if (globalSearch && pagingInfo && pagingInfo.hasNextPage && pagingInfo.continuationToken) {
			getFulltextSearch({
				dateFrom: searchDateRange.from,
				dateTo: searchDateRange.to,
				searchTypes: searchTypes,
				fulltext: globalSearch,
				continuationToken: pagingInfo.continuationToken,
			}, false);
		}
	},[ globalSearch, searchTypes, searchDateRange, pagingInfo ]);


	useEffect(() => {
		if (data) {
			updateOrdersAndPagingInfo(data);
		}
	}, [ data ]);


	useEffect(() => {
		if (soData) {
			updateOrder(soData);
		}
	}, [ soData ]);

	
	const resetAllApi = useCallback(() => {
		getFulltextSearchInit();
		resetSingleOrder();
		resetCustomerOrders();
		resetCustomerInfo();
		resetOrderTimeline();
	},[ getFulltextSearchInit ]);


	return (
		<AppPage
			isError={isError || soIsError}
			isLoading={isLoading || soIsLoading || soIsFetching || isFetching}
			className='search-page orders-page'
			pageLabel='Vyhledávání'
			resetPageApi={resetAllApi}
			ref={pageRef}
			onScroll={(event: UIEvent<HTMLDivElement>) => {
				const bottom = event.currentTarget.scrollHeight - event.currentTarget.scrollTop < event.currentTarget.clientHeight + 400;
				setIsScrolledDown(bottom);
				setOrdersScrollTop(event.currentTarget.scrollTop);
				setOrdersTop(event.currentTarget.getBoundingClientRect().top);
				setOrdersHeight(event.currentTarget.clientHeight);
			}}
		>
			<div className='orders-content'>
				
				<ListArea isSmallScreen={isSmallScreen} pagingLoading={data && data.pagingInfo.hasNextPage && isFetching ? true : false} />
				
				{!isSmallScreen &&
					<Detail
						dateWithoutTime={dateWithoutTime}
						isSmallScreen={isSmallScreen}
						ordersScrollTop={ordersScrollTop}
						ordersTop={ordersTop}
						ordersHeight={ordersHeight} />}
			</div>
		
		</AppPage>
	)
}

export default Search;